/**
 * Centralized date utilities for timezone-safe date handling
 *
 * THE PROBLEM:
 * JavaScript's `new Date('YYYY-MM-DD')` interprets date-only strings as UTC midnight.
 * When `toLocaleDateString()` is called, it converts to local time.
 * For users in timezones west of UTC (e.g., US Pacific UTC-8), this shows
 * the wrong day: "2026-02-20" at UTC midnight becomes "Feb 19" at 4pm local.
 *
 * THE SOLUTION:
 * These utilities parse date-only strings as LOCAL midnight, ensuring the
 * displayed date matches the database date regardless of user timezone.
 *
 * @module date-utils
 */

/**
 * Parse a date string as LOCAL time (not UTC).
 *
 * This function safely handles both:
 * - Date-only strings ('2026-02-20') - parsed as local midnight
 * - ISO datetime strings ('2026-02-20T18:00:00Z') - parsed normally
 *
 * @param dateStr - Date string from database
 * @returns Date object in local time
 *
 * @example
 * // Date-only string: parsed as local midnight
 * parseLocalDate('2026-02-20') // Feb 20, 2026 00:00:00 local time
 *
 * // ISO datetime: parsed as specified timezone
 * parseLocalDate('2026-02-20T18:00:00Z') // Feb 20, 2026 (converted to local)
 */
export function parseLocalDate(
  dateStr: string | Date | null | undefined,
): Date | null {
  if (!dateStr) return null;

  // Already a Date object
  if (dateStr instanceof Date) {
    return dateStr;
  }

  // ISO datetime with time component - parse normally
  if (dateStr.includes('T') || dateStr.includes(' ')) {
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? null : d;
  }

  // Date-only string (YYYY-MM-DD) - append T00:00:00 to force local interpretation
  // Without the time component, JS would interpret as UTC midnight
  const d = new Date(`${dateStr}T00:00:00`);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Today's local calendar date as a `YYYY-MM-DD` string.
 *
 * Built from local date components (NOT `toISOString()`, which is UTC and
 * would report the wrong day for users east/west of UTC near midnight).
 * The result is directly comparable against PostgreSQL `date` columns
 * (`closing_date`, `audition_dates.start_date`) because date-only strings
 * sort lexicographically the same as chronologically.
 *
 * @param now - injectable clock for tests (default: `new Date()`)
 * @returns local date as `YYYY-MM-DD`
 *
 * @example
 * localTodayISODate() // "2026-06-30"
 */
export function localTodayISODate(now: Date = new Date()): string {
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Format a date for display with timezone safety.
 *
 * @param date - Date string or Date object
 * @param options - Intl.DateTimeFormat options
 * @param locale - BCP 47 locale identifier (default: en-US)
 * @returns Formatted date string or '-' if date is null
 *
 * @example
 * formatLocalDate('2026-02-20') // "February 20, 2026"
 * formatLocalDate('2026-02-20', { weekday: 'short' }) // "Thu, February 20, 2026"
 */
export function formatLocalDate(
  date: string | Date | null | undefined,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  },
  locale: string = 'en-US',
): string {
  const parsed = parseLocalDate(date);
  if (!parsed) return '-';

  return parsed.toLocaleDateString(locale, options);
}

/**
 * Format date with long format (January 15, 2026)
 */
export function formatDateLong(
  date: string | Date | null | undefined,
  locale: string = 'en-US',
): string {
  return formatLocalDate(
    date,
    { year: 'numeric', month: 'long', day: 'numeric' },
    locale,
  );
}

/**
 * Format date with short format (Jan 15, 2026)
 */
export function formatDateShort(
  date: string | Date | null | undefined,
  locale: string = 'en-US',
): string {
  return formatLocalDate(
    date,
    { year: 'numeric', month: 'short', day: 'numeric' },
    locale,
  );
}

/**
 * Format date with weekday (Fri, Jan 15, 2026)
 */
export function formatDateWithWeekday(
  date: string | Date | null | undefined,
  locale: string = 'en-US',
): string {
  return formatLocalDate(
    date,
    { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' },
    locale,
  );
}

/**
 * Format date with full weekday (Friday, January 15, 2026)
 */
export function formatDateWithFullWeekday(
  date: string | Date | null | undefined,
  locale: string = 'en-US',
): string {
  return formatLocalDate(
    date,
    { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' },
    locale,
  );
}

/**
 * Format date in EU style (15 Jan 2026)
 */
export function formatDateEU(
  date: string | Date | null | undefined,
  locale: string = 'en-GB',
): string {
  return formatLocalDate(
    date,
    { year: 'numeric', month: 'short', day: 'numeric' },
    locale,
  );
}

/**
 * Format date for Fever API (4-nov-2025)
 */
export function formatDateFever(
  date: string | Date | null | undefined,
): string {
  const parsed = parseLocalDate(date);
  if (!parsed) return '-';

  const day = parsed.getDate();
  const month = parsed
    .toLocaleDateString('en-GB', { month: 'short' })
    .toLowerCase();
  const year = parsed.getFullYear();

  return `${day}-${month}-${year}`;
}

/**
 * Format date range (Jan 15 - Jan 20, 2026)
 */
export function formatDateRange(
  startDate: string | Date | null | undefined,
  endDate: string | Date | null | undefined,
  locale: string = 'en-US',
): string {
  const start = parseLocalDate(startDate);
  const end = parseLocalDate(endDate);

  if (!start) return '-';
  if (!end) return formatDateShort(startDate, locale);

  // Same day
  if (start.toDateString() === end.toDateString()) {
    return formatDateShort(startDate, locale);
  }

  // Same year - don't repeat year
  if (start.getFullYear() === end.getFullYear()) {
    const startStr = start.toLocaleDateString(locale, {
      month: 'short',
      day: 'numeric',
    });
    const endStr = end.toLocaleDateString(locale, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    return `${startStr} - ${endStr}`;
  }

  // Different years
  return `${formatDateShort(startDate, locale)} - ${formatDateShort(endDate, locale)}`;
}

/**
 * Get relative date description (Today, Tomorrow, Yesterday, or formatted date)
 */
export function getRelativeDateLabel(
  date: string | Date | null | undefined,
  locale: string = 'en-US',
): string {
  const parsed = parseLocalDate(date);
  if (!parsed) return '-';

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const target = new Date(parsed);
  target.setHours(0, 0, 0, 0);

  const diffDays = Math.round(
    (target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
  );

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays === -1) return 'Yesterday';

  return formatDateWithWeekday(date, locale);
}

/**
 * Check if a date is today
 */
export function isToday(date: string | Date | null | undefined): boolean {
  const parsed = parseLocalDate(date);
  if (!parsed) return false;

  const today = new Date();
  return (
    parsed.getDate() === today.getDate() &&
    parsed.getMonth() === today.getMonth() &&
    parsed.getFullYear() === today.getFullYear()
  );
}

/**
 * Check if a date is in the past
 */
export function isPast(date: string | Date | null | undefined): boolean {
  const parsed = parseLocalDate(date);
  if (!parsed) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return parsed < today;
}

/**
 * Check if a date is in the future
 */
export function isFuture(date: string | Date | null | undefined): boolean {
  const parsed = parseLocalDate(date);
  if (!parsed) return false;

  const today = new Date();
  today.setHours(23, 59, 59, 999);

  return parsed > today;
}

/**
 * Format a timestamp (datetime) for display.
 * Unlike formatLocalDate, this preserves the time component for proper timezone conversion.
 *
 * @param timestamp - ISO datetime string (e.g., '2026-02-20T18:00:00Z')
 * @param options - Intl.DateTimeFormat options
 * @param locale - BCP 47 locale identifier
 * @returns Formatted datetime string
 */
export function formatTimestamp(
  timestamp: string | Date | null | undefined,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  },
  locale: string = 'en-US',
): string {
  if (!timestamp) return '-';

  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  if (isNaN(date.getTime())) return '-';
  return date.toLocaleString(locale, options);
}

/**
 * Format date portion of a timestamp (datetime) for display.
 * This converts the timestamp to local time and formats only the date.
 *
 * Use this when you have a full timestamp but only want to display the date.
 *
 * @param timestamp - ISO datetime string (e.g., '2026-02-20T18:00:00Z')
 * @param options - Intl.DateTimeFormat options
 * @param locale - BCP 47 locale identifier
 * @returns Formatted date string
 */
export function formatTimestampDate(
  timestamp: string | Date | null | undefined,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  },
  locale: string = 'en-US',
): string {
  if (!timestamp) return '-';

  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  if (isNaN(date.getTime())) return '-';
  return date.toLocaleDateString(locale, options);
}
